import React, { Suspense, lazy, useEffect, useState, useMemo, useCallback } from 'react';
import Header from '../Header';
import { Switch, Route } from 'react-router-dom';
import Home from 'views/Home';
import { Loader, RouteWithPermissionsCheck, Notification } from 'components';
import { useLocation, useHistory } from 'react-router-dom';
import { setSelectedChat } from 'store/chat/chat.actions';
import { useDispatch, useSelector } from 'react-redux';
import useChatConnections from 'hooks/redux/useChatConnections';
import useTaskConnections from 'hooks/redux/useTaskConnections';
import useCall from 'hooks/redux/useCall';
import './main.scss'
import ChatRoutes from 'views/Chat/chat-routes';
import Tasks from 'views/Tasks';
import { setCSSVar } from 'utils';
import SystemMessages from 'views/Header/SystemMessages';
import classNames from 'classnames';
import useHasFeature, { useGetFeatureConfig, AppFeatures } from 'hooks/useHasFeature';
import axios from 'axios';
import RootState from 'store/state';
import { CustomAxiosConfig } from 'services/api';
import loadExternalDirectoryContacts from "hooks/redux/loadExternalDirectoryContacts";
import loadTeamsContacts from "hooks/redux/loadTeamsContacts";
import { clearNotification } from 'store/notification/notification.actions';
import useNotifications from 'hooks/redux/useNotifications';
import WebHIDNotificationBanner from 'views/WebHID/WebHIDNotification';
import ProcessModalDraggable from 'components/ProcessModalDraggable'
import { AccessibleAlert } from 'components/AccessibleAlert';

interface Props { }

const Main: React.FC<Props> = (props) => {
    const [classes, setClasses] = useState('');

    const { pathname } = useLocation();
    const history = useHistory();

    const dispatch = useDispatch();

    const call = useCall();

    const chat = useChatConnections();
    const tasks = useTaskConnections();

    const hasFeature = useHasFeature();
    const getFeatureConfig = useGetFeatureConfig();

    const unreadScoresCount = useSelector((state: RootState) => state.qa.unreadScoresCount) || 0;
    const showScoresAlert = hasFeature(AppFeatures.QA) && unreadScoresCount > 0;

    const chatIncoming = useChatConnections("incoming");
    const chatSelected = useChatConnections("selected");

    const taskIncoming = useTaskConnections("incoming")

    const showIncoming = () => call?.incoming || !!chatIncoming || !!taskIncoming

    const isHeaderCollapsed = () => {
        if (!(chat.length || tasks.length || showIncoming())) {
            setCSSVar("--sa-header-height", "40px")
            return true
        }

        setCSSVar("--sa-header-height", "80px")
        return false;
    }

    const isUserInCall = call?.inCall;

    useEffect(() => {
        const { navigateToDialler } = getFeatureConfig(AppFeatures.LIVE_MONITORING);
        
        if (isUserInCall && pathname !== '/' && !agentMonitoring ||
            agentMonitoring && !agentMonitoring.error && navigateToDialler) {
            history.push(`/${window.location.search}`);
        }
    }, [isUserInCall]);

    useEffect(() => {
        if (chatSelected) return;

        if (pathname === "/chat" && chatIncoming?.status === "connecting" && !chatSelected) {
            dispatch(setSelectedChat(chatIncoming.id));
        }

    }, [chatSelected, chatIncoming, pathname, dispatch]);

    const token = useSelector<RootState, string>(state => state.auth?.token!);
    const companyID = useSelector<RootState, string>(state => state.app.ID!);
    const instanceID = useSelector<RootState, string>(state => state.app.instance?.ID!);
    const isQueuedTasksProcessingModalShow = useSelector<RootState, boolean>(state => state.queuedTasks.queuedTasksProcessingShowModal);
    const agentMonitoring = useSelector((state: RootState) => state.metrics.monitoringAgent);

    const attachToken = useCallback(async (config: CustomAxiosConfig) => {
        //Check for token and is a smartagent api call
        if (!token || !config.baseURL?.includes('smartagent.app')) return config;

        if (config.ignoreToken) return config

        config.headers = {
            ...config.headers,
            'X-Amz-Security-Token': token,
        };
        config.url = config.url?.replace('{companyID}', companyID).replace('{instanceID}', instanceID);
        return config;
    }, [token, companyID, instanceID]);

    useEffect(() => {
        const interceptor = axios.interceptors.request.use(attachToken);
        return () => axios.interceptors.request.eject(interceptor);
    }, [attachToken]);

    loadExternalDirectoryContacts();

    loadTeamsContacts();

    const notification = useNotifications();

    const RealTimeMetrics = useMemo(() => lazy(() => import('views/Metrics/RealTimeMetrics')), []);
    const HistoricMetrics = useMemo(() => lazy(() => import('views/Metrics/HistoricMetrics')), []);
    const NavPlugin = useMemo(() => lazy(() => import('views/Menu/NavPlugin')), []);
    const QA = useMemo(() => lazy(() => import('views/QA')), []);
    const Settings = useMemo(() => lazy(() => import('views/Settings')), []);
    const AdminSettings = useMemo(() => lazy(() => import('views/AdminSettings')), []);
    const Case = useMemo(() => lazy(() => import('views/Case')), []);
    const CampaignManagement = useMemo(() => lazy(() => import('views/CampaignManagement')), []);
    const CaseManagement = useMemo(() => lazy(() => import('views/CaseManagement')), []);
    const QueuedTasks = useMemo(() => lazy(() => import('views/Metrics/QueuedTasks')), []);
    const AutomatedRules = useMemo(() => lazy(() => import('views/Metrics/AutomatedRules')), []);

    return (
        <main className={classNames({
            'header-collapse': isHeaderCollapsed(),
            [classes]: !!classes
        })}>
            <AccessibleAlert hidden={!showScoresAlert} text="New evaluation has been received, find details on my scores page" />
            <SystemMessages onSetClasses={(classes: string) => setClasses(classes)} />
            <WebHIDNotificationBanner />

            <Header pathname={pathname} showIncoming={showIncoming()} />

            <Suspense fallback={<div className="lazy-loading"><Loader /></div>}>
                <Switch>
                    <Route exact path="/">
                        <Home />
                    </Route>
                    <RouteWithPermissionsCheck path="/chat" component={ChatRoutes} appFeature={AppFeatures.CHAT} />
                    <RouteWithPermissionsCheck path="/metrics/real-time" component={RealTimeMetrics} appFeature={AppFeatures.REALTIME_METRICS} />
                    <RouteWithPermissionsCheck path="/metrics/historic" component={HistoricMetrics} appFeature={AppFeatures.HISTORIC_METRICS} />
                    <RouteWithPermissionsCheck path="/metrics/queued-tasks" component={QueuedTasks} appFeature={AppFeatures.QUEUED_TASKS} />
                    <RouteWithPermissionsCheck path="/metrics/automated-rules" component={AutomatedRules} appFeature={AppFeatures.AUTOMATED_RULES} />
                    <Route path="/metrics/:plugin" component={NavPlugin} />
                    <RouteWithPermissionsCheck path="/qa" component={QA} appFeature={AppFeatures.QA} />
                    <Route path="/admin-settings" component={AdminSettings} />
                    <Route path="/settings" component={Settings} />
                    {hasFeature(AppFeatures.CASE_MANAGEMENT) && <Route exact path="/case-management" component={CaseManagement} />}
                    {hasFeature(AppFeatures.CASE) && <Route path="/case/:caseID" component={Case} />}
                    {hasFeature(AppFeatures.CASE) && <Route path="/case" component={Case} />}
                    {hasFeature(AppFeatures.CAMPAIGN_MANAGEMENT) && <Route path="/campaignmanagement" component={CampaignManagement} />}
                    <Route path="/tasks" component={Tasks} />
                    <RouteWithPermissionsCheck path="/task" component={Tasks} appFeature={AppFeatures.TASK_HISTORY} />
                </Switch>
            </Suspense>
            {notification && (
                <Notification
                    header={notification.header}
                    text={notification.text}
                    type={notification.type}
                    closeAfterMs={notification.closeAfterMs}
                    onClose={() => dispatch(clearNotification())}
                />
            )}

            {isQueuedTasksProcessingModalShow &&
                <ProcessModalDraggable />
            }
        </main >
    )
}

export default React.memo(Main);
